import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, Divider, FormControlLabel,
  IconButton, Paper, Popover, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GridViewIcon from '@mui/icons-material/GridView';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewListIcon from '@mui/icons-material/ViewList';
import { DragDropContext, DragStart, Draggable, Droppable, DropResult, DraggableProvided } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateEpic, Epic } from '../../store/slices/epicsSlice';
import {
  addWorkstreamSprint,
  updateWorkstreamSprint,
  selectSprintsForWorkstream,
  WorkstreamSprint,
} from '../../store/slices/sprintsSlice';
import type { Story } from '../../store/slices/storiesSlice';
import { PARENT_STATE_COLORS } from '../../store/slices/stateManagementSlice';
import { formatDate, getSprintDateRange } from '../../utils/sprintDateUtils';

// ── Constants ────────────────────────────────────────────────────────────────

const BACKLOG_DROPPABLE = 'sprint-planner-backlog';
const SPRINT_COL_DROPPABLE_PREFIX = 'sprint-drop-';
const SPRINT_CARD_DRAGGABLE_PREFIX = 'sprint-card-';

// ── Backlog table column definitions ─────────────────────────────────────────

interface BacklogColumnDef {
  id: string;
  label: string;
}

const BACKLOG_STORY_COLUMNS: BacklogColumnDef[] = [
  { id: 'epic', label: 'Epic' },
  { id: 'points', label: 'Points' },
  { id: 'type', label: 'Type' },
  { id: 'state', label: 'State' },
  { id: 'sprint', label: 'Sprint' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Find the sprint that currently owns a story. */
const findStorySprintId = (
  storyId: string,
  sprints: WorkstreamSprint[],
): string | undefined => sprints.find((s) => s.storyIds.includes(storyId))?.id;

/** Shared drag-ghost renderer: a compact ArticleIcon + title badge that
 *  follows the cursor, replacing the default full-element clone. */
const StoryDragClone: React.FC<{
  provided: DraggableProvided;
  title: string;
}> = ({ provided, title }) => (
  <Box
    ref={provided.innerRef}
    {...provided.draggableProps}
    {...provided.dragHandleProps}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      bgcolor: 'primary.main',
      color: '#fff',
      boxShadow: 6,
      maxWidth: 260,
      userSelect: 'none',
      ...provided.draggableProps.style,
    }}
  >
    <ArticleIcon fontSize="small" />
    <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#fff' }}>
      {title || '(untitled)'}
    </Typography>
  </Box>
);

// ── Story card inside a sprint container ─────────────────────────────────────

interface SprintStoryCardProps {
  story: Story;
  epicTitle: string;
  onRemove: () => void;
  isSprintClosed?: boolean;
  isStoryClosed?: boolean;
}

const SprintStoryCard: React.FC<SprintStoryCardProps> = ({ story, epicTitle, onRemove, isSprintClosed, isStoryClosed }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      opacity: isStoryClosed ? 0.65 : 1,
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ textDecoration: isStoryClosed ? 'line-through' : 'none', color: isStoryClosed ? 'text.disabled' : 'inherit' }}
      >
        {story.title || '(untitled)'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
        <Chip label={epicTitle} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        {(story.points ?? 0) > 0 && (
          <Chip
            label={`${story.points} pts`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 18 }}
          />
        )}
        {isStoryClosed ? (
          <Chip label="Closed" size="small" sx={{ fontSize: '0.65rem', height: 18, color: 'text.disabled', bgcolor: 'action.disabledBackground' }} />
        ) : story.status ? (
          <Chip label={story.status} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
        ) : null}
      </Box>
    </Box>
    {!isSprintClosed && !isStoryClosed && (
      <Tooltip title="Remove from sprint">
        <IconButton size="small" onClick={onRemove} aria-label="Remove from sprint">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Paper>
);

// ── Main component ────────────────────────────────────────────────────────────

const WorkstreamSprintPlanner: React.FC = () => {
  const navigate = useNavigate();
  const { workstreamId } = useParams<{ workstreamId: string }>();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // ── Redux state ────────────────────────────────────────────────────────────
  const workstream = useAppSelector((state) =>
    state.workstreams.workstreams.find((w) => w.id === workstreamId),
  );
  const allSprints = useAppSelector((state) =>
    workstreamId ? selectSprintsForWorkstream(state, workstreamId) : [],
  );
  const allEpics = useAppSelector((state) => state.epics.epics);
  const sprintsConfig = useAppSelector((state) => state.sprints);

  // ── Workstream-scoped epics ────────────────────────────────────────────────
  const workstreamEpics = useMemo(
    () => allEpics.filter((e) => e.workstreamId === workstreamId).sort((a, b) => a.priority - b.priority),
    [allEpics, workstreamId],
  );

  // ── Sprint visibility & order ──────────────────────────────────────────────
  // sprintOrder holds the sprint IDs in display order
  const [sprintOrder, setSprintOrder] = useState<string[]>(() => allSprints.map((s) => s.id));
  const [hiddenSprintIds, setHiddenSprintIds] = useState<Set<string>>(new Set());

  // Keep sprintOrder in sync when allSprints gains or loses entries
  const currentSprintIds = allSprints.map((s) => s.id);
  React.useEffect(() => {
    setSprintOrder((prev) => {
      const existingInOrder = prev.filter((id) => currentSprintIds.includes(id));
      const newIds = currentSprintIds.filter((id) => !prev.includes(id));
      if (newIds.length === 0 && existingInOrder.length === prev.length) return prev;
      return [...existingInOrder, ...newIds];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSprints.length, allSprints.map((s) => s.id).join(',')]);

  /** Ordered, visibility-filtered sprints for display */
  const visibleSprints = useMemo(
    () =>
      sprintOrder
        .map((id) => allSprints.find((s) => s.id === id))
        .filter((s): s is WorkstreamSprint => s !== undefined && !hiddenSprintIds.has(s.id)),
    [sprintOrder, allSprints, hiddenSprintIds],
  );

  // ── View mode ──────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    () => (localStorage.getItem('sprintPlannerViewMode') as 'grid' | 'list') || 'grid',
  );

  // ── Sprint filter popover ──────────────────────────────────────────────────
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  const handleSprintFilterDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const newOrder = Array.from(sprintOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setSprintOrder(newOrder);
  };

  const toggleSprintVisibility = (sprintId: string) => {
    setHiddenSprintIds((prev) => {
      const next = new Set(prev);
      if (next.has(sprintId)) {
        next.delete(sprintId);
      } else {
        next.add(sprintId);
      }
      return next;
    });
  };

  // ── Expanded epics (in the backlog table) ──────────────────────────────────
  // (removed — backlog now shows a flat story list)

  // ── Backlog table: search, sort, column visibility ─────────────────────────
  const [backlogSearch, setBacklogSearch] = useState('');
  const [backlogSortCol, setBacklogSortCol] = useState<string | null>(null);
  const [backlogSortDir, setBacklogSortDir] = useState<'asc' | 'desc'>('asc');
  const [backlogVisibleCols, setBacklogVisibleCols] = useState<Set<string>>(
    () => new Set(BACKLOG_STORY_COLUMNS.map((c) => c.id)),
  );
  const [backlogColAnchorEl, setBacklogColAnchorEl] = useState<HTMLElement | null>(null);

  // ── Active drag tracking ───────────────────────────────────────────────────
  // When renderClone is used, the source Draggable's dragSnapshot.isDragging is
  // false. Track the dragging item's ID here so we can highlight the source.
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);

  const handleBacklogSortClick = (col: string) => {
    if (backlogSortCol === col) {
      if (backlogSortDir === 'asc') {
        setBacklogSortDir('desc');
      } else {
        setBacklogSortCol(null);
        setBacklogSortDir('asc');
      }
    } else {
      setBacklogSortCol(col);
      setBacklogSortDir('asc');
    }
  };

  const toggleBacklogColumn = (colId: string) => {
    setBacklogVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  };

  // ── Flat story list (all stories in this workstream) ──────────────────────
  const allWorkstreamStories = useMemo(
    () =>
      workstreamEpics.flatMap((epic) =>
        (epic.stories ?? []).map((story) => ({ story, epic })),
      ),
    [workstreamEpics],
  );

  const filteredBacklogStories = useMemo(() => {
    const q = backlogSearch.toLowerCase().trim();
    const filtered = q
      ? allWorkstreamStories.filter(
        ({ story, epic }) =>
          story.title.toLowerCase().includes(q) ||
          epic.title.toLowerCase().includes(q),
      )
      : allWorkstreamStories;

    if (!backlogSortCol) return filtered;

    return [...filtered].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      if (backlogSortCol === 'title') {
        aVal = a.story.title;
        bVal = b.story.title;
      } else if (backlogSortCol === 'epic') {
        aVal = a.epic.title;
        bVal = b.epic.title;
      } else if (backlogSortCol === 'points') {
        aVal = a.story.points ?? 0;
        bVal = b.story.points ?? 0;
      } else if (backlogSortCol === 'type') {
        aVal = a.story.engineeringSubtype ?? a.story.type;
        bVal = b.story.engineeringSubtype ?? b.story.type;
      } else if (backlogSortCol === 'state') {
        aVal = a.story.parentState ?? '';
        bVal = b.story.parentState ?? '';
      } else if (backlogSortCol === 'sprint') {
        const aSprintId = findStorySprintId(a.story.id, allSprints);
        const bSprintId = findStorySprintId(b.story.id, allSprints);
        aVal = aSprintId ? (allSprints.find((s) => s.id === aSprintId)?.number ?? 0) : 0;
        bVal = bSprintId ? (allSprints.find((s) => s.id === bSprintId)?.number ?? 0) : 0;
      }
      if (aVal < bVal) return backlogSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return backlogSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allWorkstreamStories, backlogSearch, backlogSortCol, backlogSortDir, allSprints]);

  // ── Add sprint ─────────────────────────────────────────────────────────────
  const handleAddSprint = () => {
    if (!workstreamId) return;
    const nextNumber = allSprints.length + 1;
    const newSprint: WorkstreamSprint = {
      id: uuidv4(),
      workstreamId,
      number: nextNumber,
      storyIds: [],
    };
    dispatch(addWorkstreamSprint(newSprint));
    // sprintOrder sync will pick up the new id on next render via the sync block above
  };

  // ── Story assignment helpers ───────────────────────────────────────────────
  /** Assign story to a sprint: update story.sprintId in its epic, update sprint.storyIds */
  const assignStory = (story: Story, epic: Epic, targetSprintId: string) => {
    const targetSprint = allSprints.find((s) => s.id === targetSprintId);
    if (!targetSprint) return;

    // Update story.sprintId inside the epic
    const updatedStories = (epic.stories ?? []).map((s) =>
      s.id === story.id ? { ...s, sprintId: targetSprintId } : s,
    );
    dispatch(updateEpic({ ...epic, stories: updatedStories }));

    // Add to sprint.storyIds (avoid duplicates)
    if (!targetSprint.storyIds.includes(story.id)) {
      dispatch(updateWorkstreamSprint({ ...targetSprint, storyIds: [...targetSprint.storyIds, story.id] }));
    }
  };

  /** Remove story from whichever sprint currently owns it */
  const unassignStory = (story: Story, epic: Epic) => {
    const ownerSprint = allSprints.find((s) => s.storyIds.includes(story.id));

    // Clear story.sprintId in the epic
    const updatedStories = (epic.stories ?? []).map((s) =>
      s.id === story.id ? { ...s, sprintId: null } : s,
    );
    dispatch(updateEpic({ ...epic, stories: updatedStories }));

    // Remove from sprint.storyIds
    if (ownerSprint) {
      dispatch(updateWorkstreamSprint({
        ...ownerSprint,
        storyIds: ownerSprint.storyIds.filter((id) => id !== story.id),
      }));
    }
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  const handleDragStart = (initial: DragStart) => {
    setDraggingItemId(initial.draggableId);
  };

  const handleDragEnd = (result: DropResult) => {
    setDraggingItemId(null);
    if (!result.destination) return;
    const { source, destination, draggableId } = result;

    const destId = destination.droppableId;
    const srcId = source.droppableId;

    // Only process story drags (draggableId is story.id or sprint-card-{story.id})
    if (srcId === BACKLOG_DROPPABLE || srcId.startsWith(SPRINT_COL_DROPPABLE_PREFIX)) {
      const storyId = draggableId.startsWith(SPRINT_CARD_DRAGGABLE_PREFIX)
        ? draggableId.slice(SPRINT_CARD_DRAGGABLE_PREFIX.length)
        : draggableId;
      // Find the story and its epic across all workstream epics
      let foundStory: Story | undefined;
      let foundEpic: Epic | undefined;
      for (const epic of workstreamEpics) {
        const s = (epic.stories ?? []).find((st) => st.id === storyId);
        if (s) { foundStory = s; foundEpic = epic; break; }
      }
      if (!foundStory || !foundEpic) return;

      if (destId.startsWith(SPRINT_COL_DROPPABLE_PREFIX)) {
        const targetSprintId = destId.replace(SPRINT_COL_DROPPABLE_PREFIX, '');

        // Prevent dropping into a closed sprint
        const targetSprint = allSprints.find((s) => s.id === targetSprintId);
        if (targetSprint?.status === 'Closed') return;

        // If story already belongs to a different sprint, remove it from that one first
        const currentOwner = findStorySprintId(storyId, allSprints);
        if (currentOwner && currentOwner !== targetSprintId) {
          const ownerSprint = allSprints.find((s) => s.id === currentOwner);
          if (ownerSprint) {
            dispatch(updateWorkstreamSprint({
              ...ownerSprint,
              storyIds: ownerSprint.storyIds.filter((id) => id !== storyId),
            }));
          }
        }

        assignStory(foundStory, foundEpic, targetSprintId);
      } else if (destId === BACKLOG_DROPPABLE) {
        // Dragged back to backlog → unassign
        unassignStory(foundStory, foundEpic);
      }
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getEpicForStory = (storyId: string): Epic | undefined =>
    workstreamEpics.find((e) => (e.stories ?? []).some((s) => s.id === storyId));

  // ── Guard ──────────────────────────────────────────────────────────────────
  if (!workstream) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Workstream not found.</Typography>
        <IconButton onClick={() => navigate('/epic-planner')} aria-label="Back to Epic Planner">
          <ArrowBackIosNewIcon />
        </IconButton>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Box>
        {/* ── Toolbar ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Left: Add Story, Add Sprint */}
          <Box sx={{ display: 'flex', gap: 1 }}>

            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={handleAddSprint}
            >
              Add Sprint
            </Button>
          </Box>

          {/* Right: Sprint filter popover + view toggle */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Sprint filter / reorder */}
            <Tooltip title="Configure sprints">
              <IconButton
                size="medium"
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                aria-label="Configure sprint visibility and order"
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* View toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_e, val) => {
                if (val) {
                  setViewMode(val);
                  localStorage.setItem('sprintPlannerViewMode', val);
                }
              }}
              aria-label="sprint view mode"
            >
              <Tooltip title="Stacked View">
                <ToggleButton value="list" aria-label="stacked view">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Grid View">
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* ── Sprint filter popover ── */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, minWidth: 240 }}>
            <Typography variant="subtitle2" gutterBottom>
              Sprint Visibility &amp; Order
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Check to show · Drag to reorder
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {allSprints.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sprints yet.
              </Typography>
            ) : (
              <DragDropContext onDragEnd={handleSprintFilterDragEnd}>
                <Droppable droppableId="sprint-filter-order">
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {sprintOrder
                        .map((id) => allSprints.find((s) => s.id === id))
                        .filter((s): s is WorkstreamSprint => s !== undefined)
                        .map((sprint, index) => (
                          <Draggable key={sprint.id} draggableId={`filter-${sprint.id}`} index={index}>
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                              >
                                <Box
                                  {...drag.dragHandleProps}
                                  sx={{ cursor: 'grab', display: 'flex', mr: 0.5, color: 'text.secondary' }}
                                >
                                  <DragIndicatorIcon fontSize="small" />
                                </Box>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={!hiddenSprintIds.has(sprint.id)}
                                      onChange={() => toggleSprintVisibility(sprint.id)}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      Sprint {sprint.number}{sprint.nickname ? ` · ${sprint.nickname}` : ''}
                                    </Typography>
                                  }
                                  sx={{ m: 0, flex: 1 }}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Box>
        </Popover>

        {/* ── Sprint containers ── */}
        {allSprints.length === 0 ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No sprints yet. Use <strong>Add Sprint</strong> to create your first sprint.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={
              viewMode === 'grid'
                ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 2,
                  mb: 4,
                }
                : { display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }
            }
          >
            {visibleSprints.map((sprint) => {
              const dateRange = getSprintDateRange(
                sprintsConfig.startDate,
                sprintsConfig.sprintLength,
                sprint.number,
              );
              const assignedStories: { story: Story; epic: Epic }[] = sprint.storyIds
                .map((sid) => {
                  const epic = getEpicForStory(sid);
                  if (!epic) return null;
                  const story = (epic.stories ?? []).find((s) => s.id === sid);
                  if (!story) return null;
                  return { story, epic };
                })
                .filter((x): x is { story: Story; epic: Epic } => x !== null);

              const totalPts = assignedStories.reduce((sum, { story }) => sum + (story.points ?? 0), 0);
              const isClosed = sprint.status === 'Closed';
              const closedBorderColor = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400];
              const closedBgColor = theme.palette.mode === 'dark'
                ? alpha(theme.palette.grey[800], 0.6)
                : alpha(theme.palette.grey[500], 0.08);

              return (
                <Paper
                  key={sprint.id}
                  elevation={isClosed ? 0 : 2}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: isClosed ? 0.72 : 1,
                    transition: 'opacity 0.15s ease',
                    ...(isClosed && {
                      border: '1.5px dashed',
                      borderColor: closedBorderColor,
                      bgcolor: closedBgColor,
                    }),
                  }}
                >
                  {/* Sprint header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ flexGrow: 1, color: isClosed ? 'text.disabled' : 'inherit' }}
                    >
                      Sprint {sprint.number}{sprint.nickname ? ` · ${sprint.nickname}` : ''}
                    </Typography>
                    {isClosed && (
                      <Chip
                        icon={<LockOutlinedIcon sx={{ fontSize: '0.75rem !important' }} />}
                        label="Closed"
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          color: 'text.disabled',
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                          '& .MuiChip-icon': { color: 'text.disabled' },
                        }}
                      />
                    )}
                    <Chip
                      label={`${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', color: isClosed ? 'text.disabled' : 'inherit' }}
                    />
                    <Chip
                      label={`${totalPts} pts`}
                      size="small"
                      color={isClosed ? 'default' : 'primary'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', color: isClosed ? 'text.disabled' : 'inherit' }}
                    />
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* Drop zone for stories */}
                  <Droppable
                    droppableId={`${SPRINT_COL_DROPPABLE_PREFIX}${sprint.id}`}
                    isDropDisabled={isClosed}
                    renderClone={(cloneProvided, _cloneSnapshot, rubric) => (
                      <StoryDragClone
                        provided={cloneProvided}
                        title={assignedStories[rubric.source.index]?.story.title ?? ''}
                      />
                    )}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          flex: 1,
                          minHeight: 80,
                          borderRadius: 1,
                          border: '1.5px dashed',
                          borderColor: isClosed
                            ? closedBorderColor
                            : snapshot.isDraggingOver ? 'primary.main' : 'divider',
                          bgcolor: isClosed
                            ? 'transparent'
                            : snapshot.isDraggingOver ? 'action.selected' : 'action.hover',
                          p: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          transition: 'border-color 0.15s, background-color 0.15s',
                          cursor: isClosed ? 'not-allowed' : 'default',
                        }}
                      >
                        {assignedStories.length === 0 && !snapshot.isDraggingOver && (
                          <Typography
                            variant="caption"
                            color={isClosed ? 'text.disabled' : 'text.disabled'}
                            sx={{ alignSelf: 'center', my: 'auto' }}
                          >
                            {isClosed ? 'Sprint is closed' : 'Drop stories here'}
                          </Typography>
                        )}
                        {assignedStories.map(({ story, epic }, idx) => {
                          const isStoryClosed = story.parentState === 'Closed';
                          return (
                            <Draggable
                              key={story.id}
                              draggableId={`${SPRINT_CARD_DRAGGABLE_PREFIX}${story.id}`}
                              index={idx}
                              isDragDisabled={isClosed || isStoryClosed}
                            >
                              {(dragProvided) => {
                                const isBeingDragged = draggingItemId === `${SPRINT_CARD_DRAGGABLE_PREFIX}${story.id}`;
                                return (
                                  <Box
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    style={isBeingDragged ? {} : (dragProvided.draggableProps.style ?? {})}
                                    sx={{
                                      borderRadius: 1,
                                      outline: isBeingDragged ? '2px solid' : 'none',
                                      outlineColor: isBeingDragged ? 'primary.main' : 'transparent',
                                      outlineOffset: '-2px',
                                      bgcolor: isBeingDragged ? 'action.selected' : undefined,
                                      cursor: isClosed || isStoryClosed ? 'default' : undefined,
                                    }}
                                  >
                                    <SprintStoryCard
                                      story={story}
                                      epicTitle={epic.title}
                                      onRemove={() => unassignStory(story, epic)}
                                      isSprintClosed={isClosed}
                                      isStoryClosed={isStoryClosed}
                                    />
                                  </Box>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* ── Workstream Backlog ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="overline" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Workstream Backlog
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag stories into a sprint container above to assign them. Stories remain here after being assigned.
        </Typography>

        {/* Backlog table toolbar: Add Story + search + column config */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/backlog/stories/new')}
          >
            Add Story
          </Button>
          <TextField
            size="small"
            placeholder="Search stories…"
            value={backlogSearch}
            onChange={(e) => setBacklogSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 360, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}
          />
          <Tooltip title="Configure columns">
            <IconButton
              size="small"
              onClick={(e) => setBacklogColAnchorEl(e.currentTarget)}
              aria-label="Configure backlog columns"
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Column config popover */}
        <Popover
          open={Boolean(backlogColAnchorEl)}
          anchorEl={backlogColAnchorEl}
          onClose={() => setBacklogColAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, minWidth: 180 }}>
            <Typography variant="subtitle2" gutterBottom>
              Visible Columns
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {BACKLOG_STORY_COLUMNS.map((col) => (
              <FormControlLabel
                key={col.id}
                control={
                  <Checkbox
                    size="small"
                    checked={backlogVisibleCols.has(col.id)}
                    onChange={() => toggleBacklogColumn(col.id)}
                  />
                }
                label={<Typography variant="body2">{col.label}</Typography>}
                sx={{ display: 'flex', m: 0 }}
              />
            ))}
          </Box>
        </Popover>

        {allWorkstreamStories.length === 0 ? (
          <Typography color="text.secondary">
            No stories in this workstream yet.
          </Typography>
        ) : (
          <Droppable
            droppableId={BACKLOG_DROPPABLE}
            isDropDisabled
            renderClone={(cloneProvided, _cloneSnapshot, rubric) => (
              <StoryDragClone
                provided={cloneProvided}
                title={filteredBacklogStories[rubric.source.index]?.story.title ?? ''}
              />
            )}
          >
            {(storyDrop) => (
              <TableContainer
                component={Paper}
                elevation={2}
                ref={storyDrop.innerRef}
                {...storyDrop.droppableProps}
              >
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 32 }} />{/* drag handle */}
                    <col />{/* title – remaining space */}
                    {backlogVisibleCols.has('epic') && <col style={{ width: 160 }} />}
                    {backlogVisibleCols.has('points') && <col style={{ width: 80 }} />}
                    {backlogVisibleCols.has('type') && <col style={{ width: 100 }} />}
                    {backlogVisibleCols.has('state') && <col style={{ width: 120 }} />}
                    {backlogVisibleCols.has('sprint') && <col style={{ width: 130 }} />}
                  </colgroup>
                  <TableHead sx={{ height: 44 }}>
                    <TableRow>
                      <TableCell sx={{ width: 32, p: 0 }} />
                      <TableCell>
                        <TableSortLabel
                          active={backlogSortCol === 'title'}
                          direction={backlogSortCol === 'title' ? backlogSortDir : 'asc'}
                          onClick={() => handleBacklogSortClick('title')}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      {backlogVisibleCols.has('epic') && (
                        <TableCell>
                          <TableSortLabel
                            active={backlogSortCol === 'epic'}
                            direction={backlogSortCol === 'epic' ? backlogSortDir : 'asc'}
                            onClick={() => handleBacklogSortClick('epic')}
                          >
                            Epic
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {backlogVisibleCols.has('points') && (
                        <TableCell>
                          <TableSortLabel
                            active={backlogSortCol === 'points'}
                            direction={backlogSortCol === 'points' ? backlogSortDir : 'asc'}
                            onClick={() => handleBacklogSortClick('points')}
                          >
                            Points
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {backlogVisibleCols.has('type') && (
                        <TableCell>
                          <TableSortLabel
                            active={backlogSortCol === 'type'}
                            direction={backlogSortCol === 'type' ? backlogSortDir : 'asc'}
                            onClick={() => handleBacklogSortClick('type')}
                          >
                            Type
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {backlogVisibleCols.has('state') && (
                        <TableCell>
                          <TableSortLabel
                            active={backlogSortCol === 'state'}
                            direction={backlogSortCol === 'state' ? backlogSortDir : 'asc'}
                            onClick={() => handleBacklogSortClick('state')}
                          >
                            State
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {backlogVisibleCols.has('sprint') && (
                        <TableCell>
                          <TableSortLabel
                            active={backlogSortCol === 'sprint'}
                            direction={backlogSortCol === 'sprint' ? backlogSortDir : 'asc'}
                            onClick={() => handleBacklogSortClick('sprint')}
                          >
                            Sprint
                          </TableSortLabel>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBacklogStories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2 + backlogVisibleCols.size} sx={{ py: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No stories match your search.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBacklogStories.map(({ story, epic }, idx) => {
                        const assignedSprintId = findStorySprintId(story.id, allSprints);
                        const assignedSprint = assignedSprintId
                          ? allSprints.find((s) => s.id === assignedSprintId)
                          : undefined;
                        const isBeingDragged = draggingItemId === story.id;
                        const isStoryClosed = story.parentState === 'Closed';

                        return (
                          <Draggable key={story.id} draggableId={story.id} index={idx} isDragDisabled={isStoryClosed}>
                            {(dragProvided) => (
                              <TableRow
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={isBeingDragged ? {} : (dragProvided.draggableProps.style ?? {})}
                                sx={{
                                  bgcolor: isBeingDragged ? 'action.selected' : isStoryClosed ? undefined : 'action.hover',
                                  outline: isBeingDragged ? '2px solid' : undefined,
                                  outlineColor: isBeingDragged ? 'primary.main' : undefined,
                                  outlineOffset: '-2px',
                                  opacity: isStoryClosed ? 0.6 : 1,
                                  '&:hover': { bgcolor: 'action.focus' },
                                  cursor: isStoryClosed ? 'default' : 'grab',
                                }}
                              >
                                {/* Drag handle */}
                                <TableCell sx={{ p: 0, pl: 0.5, width: 32 }}>
                                  <Box
                                    {...(isStoryClosed ? {} : dragProvided.dragHandleProps)}
                                    sx={{ display: 'flex', cursor: isStoryClosed ? 'not-allowed' : 'grab', color: isStoryClosed ? 'text.disabled' : 'text.secondary' }}
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </Box>
                                </TableCell>

                                {/* Title */}
                                <TableCell
                                  sx={{ overflow: 'hidden', cursor: 'pointer' }}
                                  onClick={() => navigate(`/backlog/stories/${story.id}/edit`)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArticleIcon fontSize="small" sx={{ color: isStoryClosed ? 'text.disabled' : '#2196F3', flexShrink: 0 }} />
                                    <Typography
                                      variant="body2"
                                      noWrap
                                      sx={{
                                        textDecoration: isStoryClosed ? 'line-through' : 'none',
                                        color: isStoryClosed ? 'text.disabled' : 'inherit',
                                      }}
                                    >
                                      {story.title}
                                    </Typography>
                                    {isStoryClosed && (
                                      <Chip
                                        label="Closed"
                                        size="small"
                                        sx={{ height: 16, fontSize: '0.6rem', color: 'text.disabled', bgcolor: 'action.disabledBackground', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>

                                {/* Epic */}
                                {backlogVisibleCols.has('epic') && (
                                  <TableCell sx={{ overflow: 'hidden' }}>
                                    <Chip
                                      label={epic.title}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => navigate(`/backlog/epics/${epic.id}/edit`)}
                                      sx={{ fontSize: '0.65rem', height: 20, maxWidth: '100%' }}
                                    />
                                  </TableCell>
                                )}

                                {/* Points */}
                                {backlogVisibleCols.has('points') && (
                                  <TableCell>
                                    <Typography variant="body2" noWrap>{story.points ?? 0}</Typography>
                                  </TableCell>
                                )}

                                {/* Type */}
                                {backlogVisibleCols.has('type') && (
                                  <TableCell>
                                    <Typography variant="body2" noWrap>
                                      {story.engineeringSubtype ?? story.type}
                                    </Typography>
                                  </TableCell>
                                )}

                                {/* State */}
                                {backlogVisibleCols.has('state') && (
                                  <TableCell>
                                    {story.parentState ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                        <Chip
                                          size="small"
                                          sx={{
                                            bgcolor: PARENT_STATE_COLORS[story.parentState],
                                            color: '#fff',
                                            fontSize: '0.65rem',
                                            flexShrink: 0,
                                            width: 10,
                                            height: 10,
                                          }}
                                        />
                                        <Typography variant="body2" noWrap sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                          {story.parentState}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">—</Typography>
                                    )}
                                  </TableCell>
                                )}

                                {/* Sprint */}
                                {backlogVisibleCols.has('sprint') && (
                                  <TableCell>
                                    {assignedSprint ? (
                                      <Chip
                                        label={`Sprint ${assignedSprint.number}${assignedSprint.nickname ? ` · ${assignedSprint.nickname}` : ''}`}
                                        size="small"
                                        color="primary"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">—</Typography>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            )}
                          </Draggable>
                        );
                      })
                    )}
                    {storyDrop.placeholder}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        )}
      </Box>
    </DragDropContext>
  );
};

export default WorkstreamSprintPlanner;
